import moment from 'moment';
import { Dialog } from '@mui/material';
import DataTable from 'react-data-table-component';
import { Link, useHistory } from 'react-router-dom';
import { CircularProgress } from "@material-ui/core";
import { useDispatch, useSelector } from 'react-redux';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Modal, ModalBody, ModalHeader } from 'reactstrap';

import './index.css';
import ViewManager from './ViewManager.js';
import AddObjectModal from './AddObjectModal.js';
import TitleRoute from '../../components/titleRoute.js';
import ImportWizard from '../../components/ImportWizard.js';
import ExportWizard from '../../components/ExportWizard.js';
import TagsManager from '../../components/Tags/TagsManager.js';
import { getWorkspacePath } from '../../utils/workspaceUtils.js';
import QuickFilters from '../../components/Filters/QuickFilters.js';
import BulkActionsBar from '../../components/BulkActionsBar/index.js';
import { getQuickFilterConfig } from '../../utils/quickFiltersConfig.js';
import AdvancedFilterModal from '../../components/Filters/AdvancedFilter.js';
import TableAnimationFiles from '../../components/lottiefilesAnimation/TableAnimationFiles.js';
import { formatProperties, getPropertyValue, formatPropertyValue } from '../../utils/propertyFormatter.js';
import { getObjects, bulkDeleteObjects, getProperties, getAllObjectTags } from '../../store/Objects/index.js';
import { openSoftphoneAndCall } from '../../store/Phone';

const ObjectsList = ({ objectType, objectSchema }) => {
  
  const history = useHistory();
  const dispatch = useDispatch();
  const objectData = useSelector(state => state['Objects']?.[objectType]);
  const records = objectData?.['records'] || [];
  const loading = objectData?.['loading'] || false;
  const pagination = objectData?.['pagination'] || {};
  const { currentWorkspace } = useSelector(state => state['Settings']);
  const properties = objectData?.['properties'] || {};

  // States
  const [deleteIds, setDeleteIds] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isDeleteModal, setDeleteModal] = useState(false);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [isNewObjectModal, setNewObjectModal] = useState(false);
  const [importWizardOpen, setImportWizardOpen] = useState(false);
  const [exportWizardOpen, setExportWizardOpen] = useState(false);
  const [advanceFilterOpen, setAdvanceFilterOpen] = useState(false);
  const [activeView, setActiveView] = useState(null);
  const [isTagsModal, setTagsModal] = useState(false);
  const [tableKey, setTableKey] = useState(0);
  const [showTable, setShowTable] = useState(true);
  const [paginationResetToggle, setPaginationResetToggle] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [allRecordsSelected, setAllRecordsSelected] = useState(false);

  useEffect(() => {
    dispatch(getProperties());
    // Fetch records with server-side pagination
    dispatch(getObjects({ objectType, viewId: activeView?._id, limit: rowsPerPage, page: currentPage }));
    if (objectType === 'contacts' || objectType === 'companies') {
      dispatch(getAllObjectTags({ objectType }));
    }
    // Reset "select all" mode when objectType or view changes
    setAllRecordsSelected(false);
  }, [objectType, activeView]);

  useEffect(() => {
    setFilteredRecords(records);
  }, [records]);

  // ✅ Hide sidebar when import/export wizard is open
  useEffect(() => {
    if (importWizardOpen || exportWizardOpen) document.body.classList.add("workflow-open");
    else document.body.classList.remove("workflow-open");
    
    // Cleanup: remove class when component unmounts
    return () => {  document.body.classList.remove("workflow-open");};
  }, [importWizardOpen, exportWizardOpen]);

  const handleSelectedRowsChange = ({ selectedRows }) => {
    setDeleteIds([]);
    setSelectedRows(selectedRows);
    // Reset "select all records" mode when selection changes
    // This handles when user unchecks items or changes selection
    if (allRecordsSelected && selectedRows.length < filteredRecords.length) {
      setAllRecordsSelected(false);
    }
  };

  // Handler when user clicks next/previous page
  const handlePageChange = (page) => {
    setCurrentPage(page);
    setAllRecordsSelected(false); // Reset "select all" when changing pages
    dispatch(getObjects({
      objectType,
      viewId: activeView?._id,
      limit: rowsPerPage,
      page
    }));
  };

  // Handler when user changes rows per page (10, 25, 50, 100)
  const handlePerRowsChange = (newPerPage, page) => {
    setRowsPerPage(newPerPage);
    setCurrentPage(page);
    dispatch(getObjects({
      objectType,
      viewId: activeView?._id,
      limit: newPerPage,
      page
    }));
  };

  // Tag handlers
  const handleClickEditTags = () => {
    if (objectType === 'contacts' || objectType === 'companies') {
      setTagsModal(true);
    }
  };

  // Get columns based on object type or active view
  const getColumns = () => {
    const baseColumns = {
      name: {
        id: 'name',
        name: objectSchema['label'] + ' Name',
        selector: row => row['name'] || `${row['firstName'] || ''} ${row['lastName'] || ''}`.trim(),

        cell: row => {
          // Use the virtual name field from backend, with fallback for older data
          const name = row['name'] || `${row['firstName'] || ''} ${row['lastName'] || ''}`.trim() || row['email'] || 'Unnamed';
          return (
            <Link
              to={`/${objectType}/${row['_id']}`}
              title={name}
              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '13px', color: 'var(--sa-dark-text)', fontWeight: '600' }}
            >
              {name}
            </Link>
          );
        }
      },
      firstName: {
        id: 'firstName',
        name: 'First Name',
        selector: row => row['firstName'],

        cell: row => <div title={row['firstName']} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row['firstName'] || '-'}</div>
      },
      lastName: {
        id: 'lastName',
        name: 'Last Name',
        selector: row => row['lastName'],

        cell: row => <div title={row['lastName']} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row['lastName'] || '-'}</div>
      },
      email: {
        id: 'email',
        name: 'Email',
        selector: row => row['email'],

        cell: row => <div title={row['email']} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row['email']}</div>
      },
      phone: {
        id: 'phone',
        name: 'Phone',
        selector: row => row['phone'],

        cell: row => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {row['phone'] && (
              <button onClick={(e) => { e.stopPropagation(); dispatch(openSoftphoneAndCall({ phone: row['phone'], contact: { ...row, objectType } }));}}
              style={{background:'transparent',border:'none',cursor:'pointer',padding:'4px',display:'flex',alignItems:'center',flexShrink:0}} title="Call this number" >
                <svg width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.0875 17.0625C12.24 17.0625 11.3475 16.86 10.425 16.47C9.525 16.0875 8.6175 15.5625 7.7325 14.925C6.855 14.28 6.0075 13.56 5.205 12.7725C4.41 11.97 3.69 11.1225 3.0525 10.2525C2.4075 9.3525 1.89 8.4525 1.5225 7.5825C1.1325 6.6525 0.9375 5.7525 0.9375 4.905C0.9375 4.32 1.0425 3.765 1.245 3.2475C1.455 2.715 1.7925 2.22 2.25 1.7925C2.8275 1.2225 3.4875 0.9375 4.1925 0.9375C4.485 0.9375 4.785 1.005 5.04 1.125C5.3325 1.26 5.58 1.4625 5.76 1.7325L7.5 4.185C7.6575 4.4025 7.7775 4.6125 7.86 4.8225C7.9575 5.0475 8.01 5.2725 8.01 5.49C8.01 5.775 7.9275 6.0525 7.77 6.315C7.6575 6.5175 7.485 6.735 7.2675 6.9525L6.7575 7.485C6.765 7.5075 6.7725 7.5225 6.78 7.5375C6.87 7.695 7.05 7.965 7.395 8.37C7.7625 8.79 8.1075 9.1725 8.4525 9.525C8.895 9.96 9.2625 10.305 9.6075 10.59C10.035 10.95 10.3125 11.13 10.4775 11.2125L10.4625 11.25L11.01 10.71C11.2425 10.4775 11.4675 10.305 11.685 10.1925C12.0975 9.9375 12.6225 9.8925 13.1475 10.11C13.3425 10.1925 13.5525 10.305 13.7775 10.4625L16.2675 12.2325C16.545 12.42 16.7475 12.66 16.8675 12.945C16.98 13.23 17.0325 13.4925 17.0325 13.755C17.0325 14.115 16.95 14.475 16.7925 14.8125C16.635 15.15 16.44 15.4425 16.1925 15.7125C15.765 16.185 15.3 16.5225 14.76 16.74C14.2425 16.95 13.68 17.0625 13.0875 17.0625Z" fill="var(--sa-dark-text-muted)"/>
                </svg>
              </button>
            )}
            <div title={row['phone']} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row['phone']}</div>
          </div>
        )
      },
      company: {
        id: 'company',
        name: 'Company',
        selector: row => row['company'],

        cell: row => <div title={row['company']} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row['company']}</div>
      },
      title: {
        id: 'title',
        name: 'Title',
        selector: row => row['title'],

        cell: row => <div title={row['title']} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row['title']}</div>
      },
      amount: {
        id: 'amount',
        name: 'Amount', 
        selector: row => row['amount'],

        cell: row => <div title={row['amount']} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>${row['amount'] || 0}</div>
      },
      stage: {
        id: 'stage',
        name: 'Stage',
        selector: row => row['stage'],

        cell: row => <div title={row['stage']} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row['stage']}</div>
      },
      createdAt: {
        id: 'createdAt',
        name: 'Created On',
        selector: row => row['createdAt'],

        cell: row => <div title={moment(row['createdAt']).format('YY-MM-DD HH:mm')}>{moment(row['createdAt']).format('YY-MM-DD HH:mm')}</div>
      },
      updatedAt: {
        id: 'updatedAt',
        name: 'Updated On',
        selector: row => row['updatedAt'],

        cell: row => <div title={moment(row['updatedAt']).format('YY-MM-DD HH:mm')}>{moment(row['updatedAt']).format('YY-MM-DD HH:mm')}</div>
      },
      tags: {
        id: 'tags',
        name: 'Tags',
        selector: row => row['tags'],
        sortable: false,
        cell: row => {
          const tags = row['tags'] || [];
          // Filter out null/undefined tags
          const validTags = tags.filter(tag => tag != null);
          if (validTags.length === 0) return <div>-</div>;

          const defaultColor = '#6B7280'; // Default gray for legacy tags
          const displayTags = validTags.slice(0, 3);
          const remainingCount = validTags.length - 3;

          return (
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {displayTags.map((tag, index) => {
                // Handle both string and object tags
                const tagName = typeof tag === 'string' ? tag : (tag?.name || '');
                // Skip empty tag names
                if (!tagName) return null;
                // Use tag color from backend if available, otherwise use default gray
                const tagColor = typeof tag === 'string' ? defaultColor : (tag?.color || defaultColor);
                return (
                  <span
                    key={`${tagName}-${index}`}
                    style={{
                      padding: '2px 8px',
                      borderRadius: '12px',
                      backgroundColor: tagColor + '20',
                      color: tagColor,
                      fontSize: '11px',
                      fontWeight: '500',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {tagName}
                  </span>
                );
              })}
              {remainingCount > 0 && (
                <span style={{ fontSize: '11px', color: 'var(--sa-dark-text-muted)', fontWeight: '500' }}>
                  +{remainingCount} more
                </span>
              )}
            </div>
          );
        }
      }
    };

    // If a view is selected, use its columns
    if (activeView && activeView['columns'] && activeView['columns'].length > 0) {
      const viewColumns = activeView['columns']
        .filter(col => col['isVisible'] !== false) // Show columns unless explicitly hidden
        .sort((a, b) => (a['order'] || 0) - (b['order'] || 0))
        .map(col => {
          const baseCol = baseColumns[col['propertyName']];
          if (baseCol) return { ...baseCol, };
          // For custom properties not in baseColumns, create a generic column
          // Find the property definition to determine if it's a custom field
          const formattedProps = formatProperties(properties, objectType);
          const property = formattedProps.find(p => p.name === col['propertyName']);

          return {
            id: col['propertyName'],
            name: col['displayName'] || col['propertyName'].charAt(0).toUpperCase() + col['propertyName'].slice(1),
            selector: row => getPropertyValue(row, property),

            cell: row => {
              const value = getPropertyValue(row, property);
              const formatted = property ? formatPropertyValue(value, property) : (value || '-');
              return (
                <div title={formatted} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {formatted}
                </div>
              );
            }
          };
        });

      // Always ensure the name/title column is first and locked
      const nameColumnExists = viewColumns.some(col => col['id'] === 'name' || col['id'] === 'firstName');
      if (!nameColumnExists) {
        // For contacts, use firstName + lastName combination, for others use name
        if (objectType === 'contacts') {
          const nameCol = {
            id: 'fullName',
            name: 'Contact Name',
            selector: row => `${row['firstName'] || ''} ${row['lastName'] || ''}`.trim(),

            cell: row => {
              const name = `${row['firstName'] || ''} ${row['lastName'] || ''}`.trim();
              return (
                <Link
                  to={`/${objectType}/${row['_id']}`}
                  title={name}
                  style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '13px', color: 'var(--sa-dark-accent-light)', fontWeight: '600' }}
                >
                  {name || 'Unnamed Contact'}
                </Link>
              );
            }
          };
          viewColumns.unshift(nameCol);
        } else {
          viewColumns.unshift(baseColumns['name']);
        }
      } else {
        // Move name column to first position if it exists
        const nameColIndex = viewColumns.findIndex(col => col['id'] === 'name' || col['id'] === 'firstName');
        if (nameColIndex > 0) {
          const nameCol = viewColumns.splice(nameColIndex, 1)[0];
          viewColumns.unshift(nameCol);
        }
      }

      return viewColumns;
    }

    // Default columns based on object type
    let visibleColumns = [];
    if (objectType === 'contacts') {
      visibleColumns = ['name', 'firstName', 'lastName', 'email', 'phone', 'tags', 'createdAt'];
    } else if (objectType === 'companies') {
      visibleColumns = ['name', 'email', 'phone', 'tags', 'createdAt'];
    } else if (objectType === 'deals') {
      visibleColumns = ['name', 'createdAt', 'updatedAt'];
    }

    return visibleColumns.map(col => baseColumns[col]).filter(Boolean);
  };


  

  const columns = getColumns();
  // Show pagination only when total records exceed 50
  const isPaginated = pagination.total > 50;
  // Account for sticky header (~100px), filters/actions (~100px), and pagination (~60px if visible)
  const headerScrollHeight = isPaginated ? 'calc(100vh - 210px)' : 'calc(100vh - 150px)';
  const responsiveClass = isPaginated ? 'datatable--paginated' : 'datatable--fullheight object-table';

  return (
    <div className='content objects-page'>
      {importWizardOpen
        ? <div className='allemails-area'>
          <ImportWizard
            objectType={objectType}
            importWizardOpen={importWizardOpen}
            setImportWizardOpen={setImportWizardOpen}
            onComplete={() => { dispatch(getObjects({ objectType, viewId: activeView?._id, limit: rowsPerPage, page: currentPage })) }}
          />
        </div>
        : exportWizardOpen
          ? <div className='allemails-area'>
            <ExportWizard
              objectType={objectType}
              exportWizardOpen={exportWizardOpen}
              setExportWizardOpen={setExportWizardOpen}
              selectedIds={selectedRows.map(row => row['_id'])}
            />
          </div>
          : <div className='allemails-area'>
            <div className='page-title-area'>
              <div>
                <TitleRoute />
                <p className='records-title'>{pagination['total'] > 0 && `${pagination['total']} records`}</p>
              </div>
              <div className='right-btn-area'>
                <ViewManager objectType={objectType} onViewChange={(view) => setActiveView(view)} activeViewId={activeView?.['_id']} />
                <Dropdown isOpen={dropdownOpen} toggle={() => setDropdownOpen(!dropdownOpen)}>
                  <DropdownToggle caret>
                    Actions
                    <svg width="4" height="4" viewBox="0 0 4 4" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2.99316 2.46804L1.91042 0.544434L0.307735 0.544434C0.0334783 0.544434 -0.10365 0.986291 0.090615 1.24531L1.57046 3.21843C1.80758 3.53459 2.19325 3.53459 2.43037 3.21843L2.99316 2.46804Z" fill="var(--primary-color)" />
                      <path d="M3.69092 0.544434L1.91111 0.544434L2.99386 2.46804L3.9109 1.24531C4.10231 0.986291 3.96518 0.544434 3.69092 0.544434Z" fill="var(--primary-color)" />
                    </svg>
                  </DropdownToggle>
                  <DropdownMenu>
                    <DropdownItem index={0}>
                      <div className='action-item' onClick={() => history.push(getWorkspacePath('/settings?tab=crm-properties', currentWorkspace?._id))}>
                        <i className='fa fa-ticket'></i> Edit properties
                      </div>
                    </DropdownItem>
                    <DropdownItem index={0}>
                      <div className='action-item' onClick={() => setImportWizardOpen(true)}>
                        <i className='fa fa-upload'></i> Import {objectSchema['plural']}
                      </div>
                    </DropdownItem>
                    <DropdownItem index={1}>
                      <div className='action-item' onClick={() => setExportWizardOpen(true)}>
                        <i className='fa fa-download'></i> Export {objectSchema['plural']}
                      </div>
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
                <button className='btn-style-new' onClick={() => setNewObjectModal(true)}>
                  + Add {objectSchema['label']}
                </button>
              </div>
            </div>
            <div className='row'>
              <div className='col-12'>
                <div className="table-container filters-table">
                  <BulkActionsBar
                    objectType={objectType}
                    selectedItems={selectedRows.map(row => row['_id'])}
                    allRecordsSelected={allRecordsSelected}
                    onClearSelection={() => {
                      setToggleCleared(prev => !prev);
                      setSelectedRows([]);
                      setAllRecordsSelected(false); // Reset "select all" mode
                    }}
                    onRefresh={() => dispatch(getObjects({ objectType, viewId: activeView?._id, limit: rowsPerPage, page: currentPage }))}
                    onDelete={() => {
                      setDeleteIds(selectedRows.map(row => row['_id']));
                      setDeleteModal(true);
                    }}
                    totalItems={pagination.total || filteredRecords.length}
                    onSelectAll={() => {
                      // User clicked "Select all X Contacts" link
                      // Just enable "all records selected" mode
                      // (the page contacts are already selected via header checkbox)
                      setAllRecordsSelected(true);
                    }}
                    customActions={(objectType === 'contacts' || objectType === 'companies') && selectedRows.length > 0 ? [
                      {
                        label: 'Add tags',
                        icon: (
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5.0292 9.15828C4.4417 9.15828 3.85004 8.93328 3.4042 8.48745L1.5167 6.59995C1.05837 6.14162 0.816703 5.50828 0.84587 4.86245L0.94587 2.77912C0.991703 1.78328 1.7792 0.995784 2.7792 0.945784L4.86254 0.845784C5.50837 0.820784 6.1417 1.05828 6.60004 1.51662L8.48754 3.40412C9.38337 4.29995 9.38337 5.76245 8.48754 6.65828L6.65837 8.48745C6.20837 8.93328 5.62087 9.15828 5.0292 9.15828ZM1.95837 6.15412L3.84587 8.04162C4.16254 8.35828 4.58337 8.53328 5.0292 8.53328C5.47504 8.53328 5.89587 8.35828 6.21254 8.04162L8.0417 6.21245C8.35837 5.89578 8.53337 5.47495 8.53337 5.02912C8.53337 4.58328 8.35837 4.16245 8.0417 3.84578L6.1542 1.95828C5.82087 1.62495 5.35837 1.44578 4.8917 1.47078L2.80837 1.57078C2.1292 1.59995 1.60004 2.12912 1.5667 2.80412L1.4667 4.88745C1.44587 5.35828 1.62504 5.82078 1.95837 6.15412Z" fill="var(--primary-color)" />
                            <path d="M3.95829 5.31258C3.21246 5.31258 2.60413 4.70425 2.60413 3.95841C2.60413 3.21258 3.21246 2.60425 3.95829 2.60425C4.70413 2.60425 5.31246 3.21258 5.31246 3.95841C5.31246 4.70425 4.70413 5.31258 3.95829 5.31258ZM3.95829 3.22925C3.55829 3.22925 3.22913 3.55841 3.22913 3.95841C3.22913 4.35841 3.55829 4.68758 3.95829 4.68758C4.35829 4.68758 4.68746 4.35841 4.68746 3.95841C4.68746 3.55841 4.35829 3.22925 3.95829 3.22925Z" fill="var(--primary-color)" />
                          </svg>
                        ),
                        onClick: handleClickEditTags
                      }
                    ] : []}
                  />
                  {selectedRows.length === 0 && (
                    <QuickFilters
                      objectType={objectType}
                      config={getQuickFilterConfig(objectType)}
                      onAdvancedClick={() => setAdvanceFilterOpen(true)}
                      onGetData={(data) => dispatch(getObjects({ ...data, viewId: activeView?._id, limit: rowsPerPage, page: 1 }))}
                    />
                  )}
                  <style>{tableStyles}</style>
                  {showTable && (
                    <DataTable
                      key={tableKey}
                      fixedHeader
                      progressPending={loading}
                      progressComponent={<TableAnimationFiles />}
                      keyField="_id"
                      selectableRows
                      persistTableHead
                      columns={columns}
                      data={filteredRecords}
                      paginationPerPage={rowsPerPage}
                      pagination={isPaginated}
                      paginationServer={true}
                      onChangePage={handlePageChange}
                      onChangeRowsPerPage={handlePerRowsChange}
                      paginationTotalRows={pagination.total || filteredRecords.length}
                      clearSelectedRows={toggleCleared}
                      fixedHeaderScrollHeight={headerScrollHeight}
                      onSelectedRowsChange={handleSelectedRowsChange}
                      className={`table-salesai datatable--paginated`}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      paginationResetDefaultPage={paginationResetToggle}
                      onRowClicked={(row, e) => {
                        // Prevent navigation if clicking on checkbox or buttons inside row
                        if (e.target.closest('input[type="checkbox"]') || e.target.closest('button')) {
                          return;
                        }
                        history.push(getWorkspacePath(`/${objectType}/${row['_id']}`, currentWorkspace?._id));
                      }}
                      noDataComponent={
                        <div className='notfound-area'>
                          <div className='img-box'>
                            <img alt="..." src={require('../../static/img/not-found.png')} />
                          </div>
                          <p>No {objectSchema['plural'].toLowerCase()} found</p>
                        </div>
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
      }

      {/* Add Object Modal */}
      {isNewObjectModal && <AddObjectModal objectType={objectType} properties={properties} isOpen={isNewObjectModal} objectSchema={objectSchema} onClose={() => setNewObjectModal(false)} onSuccess={() => { setNewObjectModal(false); dispatch(getObjects({ objectType, viewId: activeView?._id, limit: rowsPerPage, page: currentPage })); }} />}

      {/* Delete Modal */}
      <Modal isOpen={isDeleteModal} toggle={() => setDeleteModal(false)} keyboard={true} className={`main-modal modal-dialog-centered new-user-modal`}>
        <ModalHeader toggle={() => setDeleteModal(false)}></ModalHeader>
        <ModalBody>
          <div className='content-area'>
            <div className='delete-modal'>
              <h4>
                <i className='icon'>
                  <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path d='M0 4C0 1.79086 1.79086 0 4 0H20C22.2091 0 24 1.79086 24 4V20C24 22.2091 22.2091 24 20 24H4C1.79086 24 0 22.2091 0 20V4Z' fill='#FF3B30' fill-opacity='0.15' />
                    <path d='M12 19.5C16.1421 19.5 19.5 16.1421 19.5 12C19.5 7.85786 16.1421 4.5 12 4.5C7.85786 4.5 4.5 7.85786 4.5 12C4.5 16.1421 7.85786 19.5 12 19.5Z' fill='#FF3B30' />
                    <path d='M12 9V12' stroke='#FFE2E0' strokeWidth='2' stroke-linecap='round' stroke-linejoin='round' />
                    <path d='M12 15H12.0083' stroke='#FFE2E0' strokeWidth='2' stroke-linecap='round' stroke-linejoin='round' />
                  </svg>
                </i>
                Delete {objectSchema['plural']}
              </h4>
              <p>
                {allRecordsSelected ? (
                  <>
                    Are you sure you want to delete <b>all {pagination.total || 0} {objectSchema['plural']}</b> matching the current view/filters?
                    <br />
                    <span style={{ color: '#FF3B30', fontWeight: 'bold' }}>This action cannot be undone.</span>
                  </>
                ) : (
                  <>
                    Are you sure you want to delete <b>{deleteIds.length} {objectSchema['plural']}</b>? You can't undo this action afterwards.
                  </>
                )}
              </p>
              <div className='form-area'>
                <div className='btns-box text-right'>
                  <button className='btn-style-border' onClick={() => setDeleteModal(false)}>Cancel</button>
                  <button className='btn-style-new' onClick={() => {
                    if (allRecordsSelected) {
                      // Delete ALL records using filters
                      dispatch(bulkDeleteObjects({
                        objectType,
                        deleteAll: true,
                        viewId: activeView?._id
                      }));
                    } else {
                      // Delete selected IDs only
                      dispatch(bulkDeleteObjects({ objectType, ids: deleteIds }));
                    }
                    setDeleteIds([]);
                    setSelectedRows([]);
                    setAllRecordsSelected(false);
                    setToggleCleared(prev => !prev);
                    setDeleteModal(false);
                    dispatch(getObjects({ objectType, viewId: activeView?._id, limit: rowsPerPage, page: currentPage }));
                  }}>Delete</button>
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>

      {/* Advanced Filters Modal */}
      <AdvancedFilterModal objectType={objectType} isOpen={advanceFilterOpen} onClose={() => setAdvanceFilterOpen(false)} onGetData={(data) => dispatch(getObjects({ ...data, viewId: activeView?._id, limit: rowsPerPage, page: 1 }))} />

      {/* Tags Manager Dialog (for contacts and companies) */}
      {(objectType === 'contacts' || objectType === 'companies') && isTagsModal && (
        <Dialog
          open={isTagsModal}
          onClose={() => setTagsModal(false)}
          maxWidth="sm"
          fullWidth
          slotProps={{
            paper: {
              sx: {
                borderRadius: '8px',
                minWidth: '260px',
                maxWidth: '320px',
                minHeight: '400px'
              }
            }
          }}
        >
          <TagsManager
            objectType={objectType}
            mode="select"
            selectedObjects={selectedRows}
            onClose={() => setTagsModal(false)}
            onTagsApplied={() => {
              setTagsModal(false);

              // Clear selections and reset pagination
              setToggleCleared(prev => !prev);
              setPaginationResetToggle(prev => !prev);

              // Fetch updated data which will automatically update the table
              dispatch(getObjects({ objectType, viewId: activeView?._id, limit: rowsPerPage, page: currentPage }));
            }}
          />
        </Dialog>
      )}
    </div>
  );
};

const tableStyles = `
          .table-salesai input[type="checkbox"] {
            width: 13px;
          height: 13px;
          cursor: pointer;
          accent-color: #cbd6e2;
          transform: scale(1);
          opacity: 0.45;
  }

          .table-salesai input[type="checkbox"]:checked {
            opacity: 1;
          accent-color: var(--primary-color);
  }

          .rdt_TableRow {
            transition: background-color 0.15s ease;
  }

          .rdt_TableRow:hover {
            background - color: #f5f5f5;
  }

          .table-container {
            position: relative;
  }

          /* Prevent intermittent horizontal scrollbar by clamping width to viewport */
          /* COMMENTED OUT: Allow horizontal scroll to prevent last column cutoff */
          /* .content.objects-page {
            max-width: 100vw;
            overflow-x: hidden;
          } */
          .content.objects-page {
            max-width: 100vw;
            overflow-x: auto; /* Allow horizontal scroll when needed */
          }
          /* COMMENTED OUT: Allow horizontal scroll to prevent last column cutoff */
          /* .table-container.filters-table {
            overflow-x: hidden;
          } */
          .table-container.filters-table {
            overflow-x: auto; /* Allow horizontal scroll when needed */
          }
          /* COMMENTED OUT: Allow table to expand beyond 100% for horizontal scroll */
          /* .table-salesai .rdt_Table {
            max-width: 100%;
            width: 100% !important;
            overflow-x: hidden;
          } */
          .table-salesai .rdt_Table {
            /* Remove max-width to allow table to expand when columns are wide */
            width: 100% !important;
          }
          /* COMMENTED OUT: Allow horizontal scroll to prevent last column cutoff */
          /* .table-salesai .rdt_TableBody,
          .table-salesai .rdt_TableHead {
            overflow-x: hidden;
          } */
          .table-salesai .rdt_TableBody,
          .table-salesai .rdt_TableHead {
            overflow-x: visible; /* Allow content to overflow for scrolling */
          }
          /* Ensure the scroll wrapper allows horizontal scrolling */
          .table-salesai .rdt_TableBodyScrollWrapper {
            overflow-x: auto !important;
          }
          /* Allow flex cells to shrink instead of expanding table width */
          .table-salesai .rdt_TableCol,
          .table-salesai .rdt_TableCell {
            min-width: 0 !important;
          }
          /* Ensure row and col containers allow overflow */
          .content.objects-page .row {
            overflow-x: visible;
          }
          .content.objects-page .col-12 {
            overflow-x: visible;
          }
          .table-salesai .rdt_TableCell > div {
            min-width: 0;
            max-width: 100%;
            box-sizing: border-box;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            word-break: break-word;
          }
          `;

export default ObjectsList;